import type { BootstrapData } from '../types/domain'
import { number } from './format'
import { StatusBadge } from './StatusBadge'

export function DashboardView({ data, onOpenPallets }: { data: BootstrapData; onOpenPallets: () => void }) {
  const synced = data.dashboard.sensorsByStatus.SYNCED ?? 0
  const readRequired = data.dashboard.sensorsByStatus.READ_REQUIRED ?? 0
  const coverage = data.dashboard.sensors ? Math.round((synced / data.dashboard.sensors) * 100) : 0
  const attention = data.pallets.filter(pallet => pallet.status === 'READ_REQUIRED')
  const filmBreaks = data.transportEvents.filter(event => event.type === 'FILM_BREAK' && !event.acknowledged)

  return (
    <section className="page-section">
      <div className="section-heading">
        <div><p className="eyebrow">Оперативный контур</p><h1>Состояние перевозок</h1></div>
        <p className="section-note">Демо-данные воспроизводимы. Время графиков формируется при первом запуске базы.</p>
      </div>
      <div className="metric-grid">
        <Metric title="Активные партии" value={number(data.dashboard.batches)} detail={`${data.dashboard.pallets} паллет`} tone="navy" />
        <Metric title="Датчики в системе" value={number(data.dashboard.sensors)} detail={`${coverage}% синхронизировано`} tone="blue" />
        <Metric title="Требуют считывания" value={number(readRequired)} detail={`${data.dashboard.palletsRequiringRead} паллет`} tone={readRequired ? 'amber' : 'green'} />
        <Metric title="Синхронизировано" value={number(synced)} detail="server receipt получен" tone="green" />
      </div>
      <article className={`film-alert-card ${filmBreaks.length ? 'active' : ''}`}>
        <div className="film-alert-icon" aria-hidden="true">!</div>
        <div className="film-alert-copy"><p className="eyebrow">Контроль упаковочной плёнки</p><h2>{filmBreaks.length ? `Выявлено обрывов: ${filmBreaks.length}` : 'Обрывов плёнки не выявлено'}</h2>
          <p>{filmBreaks.length ? 'События определены по синхронному падению давления на соседних угловых датчиках.' : 'Распределение давления остаётся стабильным.'}</p></div>
        <div className="film-alert-list">{filmBreaks.slice(0, 3).map(event => <div key={event.id}><strong>{event.palletNumber}</strong><span>{new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(event.occurredAt))}</span><b>{event.severity === 'CRITICAL' ? 'Критично' : 'Внимание'}</b></div>)}</div>
      </article>
      <div className="dashboard-grid">
        <article className="card attention-card">
          <div className="card-header"><div><p className="eyebrow">Очередь оператора</p><h2>Паллеты к обработке</h2></div><button className="text-button" onClick={onOpenPallets}>Все паллеты →</button></div>
          {attention.length ? attention.map(pallet => (
            <div className="attention-row" key={pallet.id}>
              <div><strong>{pallet.number}</strong><span>{pallet.batchNumber} · {pallet.cargoType}</span></div>
              <StatusBadge status={pallet.status} />
            </div>
          )) : <div className="empty-state">Нет паллет, ожидающих считывания.</div>}
        </article>
        <article className="card distribution-card">
          <div className="card-header"><div><p className="eyebrow">Парк устройств</p><h2>Статусы датчиков</h2></div></div>
          <div className="status-bars">
            {Object.entries(data.dashboard.sensorsByStatus).filter(([, value]) => value > 0).map(([status, value]) => (
              <div className="status-bar" key={status}>
                <div><StatusBadge status={status as keyof typeof data.dashboard.sensorsByStatus} /><strong>{value}</strong></div>
                <span><i style={{ width: `${(value / data.dashboard.sensors) * 100}%` }} /></span>
              </div>
            ))}
          </div>
        </article>
      </div>
    </section>
  )
}

function Metric({ title, value, detail, tone }: { title: string; value: string; detail: string; tone: string }) {
  return <article className={`metric metric-${tone}`}><span>{title}</span><strong>{value}</strong><small>{detail}</small></article>
}
